import React, { useEffect, useId, useRef } from 'react'
import { createPortal } from 'react-dom'
import PropTypes from 'prop-types'
import InlineIcon from '../InlineIcon/InlineIcon.jsx'
import './PlainModal.scss'

const FOCUSABLE =
  'a[href], area[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), iframe, [contenteditable="true"], [tabindex]:not([tabindex="-1"])'

function keepFocusInside(event, dialog) {
  if (!dialog) return
  const focusable = [...dialog.querySelectorAll(FOCUSABLE)]
  const first = focusable[0]
  const last = focusable[focusable.length - 1]
  const active = document.activeElement

  if (!dialog.contains(active)) {
    event.preventDefault()
    first.focus()
  } else if (event.shiftKey && (active === first || active === dialog)) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && active === last) {
    event.preventDefault()
    first.focus()
  }
}

/**
 * Library-neutral modal for app-wide chrome. Renders the same whether or not
 * the legacy stylesheet is active. Mount it to open, unmount it to close.
 */
function PlainModal({
  title,
  onClose,
  children,
  className = '',
  'data-testid': testId,
}) {
  const titleId = useId()
  const dialogRef = useRef(null)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    const opener = document.activeElement
    const bodyOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    dialogRef.current?.focus()

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onCloseRef.current()
      if (event.key === 'Tab') keepFocusInside(event, dialogRef.current)
    }
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = bodyOverflow
      if (opener instanceof HTMLElement && opener.isConnected) opener.focus()
    }
  }, [])

  const handleDimmerMouseDown = (event) => {
    if (event.target === event.currentTarget) onClose()
  }

  return createPortal(
    <div className='plain-modal__dimmer' onMouseDown={handleDimmerMouseDown}>
      <div
        ref={dialogRef}
        className={`plain-modal ${className}`.trim()}
        role='dialog'
        aria-modal='true'
        aria-labelledby={titleId}
        tabIndex={-1}
        data-testid={testId}
      >
        <div className='plain-modal__header'>
          <h2 id={titleId} className='plain-modal__title'>
            {title}
          </h2>
          <button
            type='button'
            className='plain-modal__close'
            aria-label='Close'
            onClick={onClose}
          >
            <InlineIcon name='close' />
          </button>
        </div>
        <div className='plain-modal__content'>{children}</div>
      </div>
    </div>,
    document.body,
  )
}

PlainModal.propTypes = {
  title: PropTypes.node.isRequired,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node,
  className: PropTypes.string,
  'data-testid': PropTypes.string,
}

export default PlainModal
